import { smsShortlistLinks } from "../lib/smsShortlistLinks";
import { buildSmsUrl, resolveTemplateForSms } from "../lib/resolveTemplate";
import { templateService } from "../lib/templateService";
import { supabase } from "../lib/supabase";
import { emailService } from "./emailService";
import { recordClientShortlistShare } from "./clientShortlistService";

export interface ShortlistSmsClient {
  id: string;
  first_name: string;
  last_name: string;
  phone?: string | null;
  email?: string | null;
}

export interface ShortlistSmsHandoffReceipt {
  client: ShortlistSmsClient;
  shortlistId: string;
  publicUrl: string;
  templatePrefilled: boolean;
}

/**
 * Records an unconfirmed SMS-composer launch, then opens the native composer.
 * It never calls an SMS provider and must not advance contact truth: opening a
 * composer is not evidence that the operator pressed Send.
 */
export async function handoffShortlistToNativeSms({
  client,
  shortlistId,
  publicUrl,
}: {
  client: ShortlistSmsClient;
  shortlistId: string;
  publicUrl: string;
}): Promise<ShortlistSmsHandoffReceipt> {
  if (!client.phone) throw new Error(`${client.first_name} does not have a mobile number.`);

  // Template failure never blocks the handoff: fall back to a bare-URL body,
  // mirroring ClientList's Send Text degrade path.
  let body = publicUrl;
  let template: Awaited<ReturnType<typeof templateService.getDefaultTemplate>> = null;
  try {
    template = await templateService.getDefaultTemplate("sms", "listing");
    if (template) {
      const config = emailService.getConfig();
      body = resolveTemplateForSms({
        template,
        client,
        agent: {
          full_name: config.agentName || null,
          email: config.agentEmail || null,
          phone: config.agentPhone || null,
        },
      }).body.replaceAll("{shortlist_url}", publicUrl);
      if (!body.includes(publicUrl)) body = `${body.trim()}\n${publicUrl}`.trim();
    }
  } catch (err) {
    console.warn("default SMS template lookup failed; texting bare link:", err);
    template = null;
    body = publicUrl;
  }

  // Auth is still required before recording anything, even though this path no
  // longer writes an activities row: the share event is an authenticated write.
  const userId = (await supabase.auth.getUser()).data.user?.id;
  if (!userId) throw new Error("Sign in again before texting a shortlist.");

  // ⚠️ NO activities ROW HERE — PKT-0385 round-2 F1 (confirmed in production).
  // Migration 031 installs trg_bump_client_last_contacted_on_activity AFTER
  // INSERT ON activities with `sent_text` in its contact-type list. It sets
  // clients.last_contacted_at = NEW.created_at from the ROW ITSELF and never
  // reads metadata, so a `send_confirmed: false` marker does not exempt it: an
  // activity written at composer-launch advances contact truth for a message
  // the operator may never send. Removing the explicit clients.update() was not
  // enough — the trigger was doing the same work invisibly.
  //
  // Attempt truth lives in client_shortlist_events via the share event below,
  // which no trigger watches. The activities feed row is written once, at
  // confirmation, by confirmShortlistSmsSent.
  //
  // Honest cost: the activity FEED no longer shows unconfirmed attempts. The
  // shortlist ledger still records every one.
  await recordClientShortlistShare(shortlistId, {
    template_prefilled: Boolean(template),
  });

  window.location.href = buildSmsUrl(client.phone, smsShortlistLinks(body)) || `sms:${client.phone.replace(/[^\d+]/g, "")}`;
  return {
    client,
    shortlistId,
    publicUrl,
    templatePrefilled: Boolean(template),
  };
}

/**
 * Promote a composer attempt to confirmed contact only after the operator says
 * the message was sent. This is deliberately separate from the native handoff:
 * browsers cannot observe whether another app actually completed a send.
 */
export async function confirmShortlistSmsSent(
  receipt: ShortlistSmsHandoffReceipt
): Promise<void> {
  const userId = (await supabase.auth.getUser()).data.user?.id;
  if (!userId) throw new Error("Sign in again before marking this text sent.");

  const contactedAt = new Date().toISOString();
  const [activityResult, clientResult] = await Promise.all([
    supabase.from("activities").insert({
      user_id: userId,
      activity_type: "sent_text",
      entity_type: "client",
      entity_id: receipt.client.id,
      metadata: {
        client_name: `${receipt.client.first_name} ${receipt.client.last_name}`,
        launch_mode: "operator_confirmation",
        send_confirmed: true,
        shortlist_id: receipt.shortlistId,
        shortlist_url: receipt.publicUrl,
        template_prefilled: receipt.templatePrefilled,
      },
    }),
    supabase.from("clients").update({ last_contacted_at: contactedAt }).eq("id", receipt.client.id),
  ]);
  if (activityResult.error) throw activityResult.error;
  if (clientResult.error) throw clientResult.error;
}
